import { createOperationDescriptor } from 'relay-runtime';
import type { IEnvironment } from 'relay-runtime';

import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { SpaceNavigationQueryReference } from './SpaceNavigationQueryReference.ts';

type Entry = {
	query: SpaceNavigationQueryReference;
	readers: number;
};

/**
 * Preload-to-render handoff, scoped to the Relay environment. Only the most recent
 * unclaimed preload is retained; mounted readers keep their own entries alive.
 */
export class SpaceNavigationQueryOwner {
	private entries = new Map<string, Entry>();
	private entriesByReference = new WeakMap<SpaceNavigationQueryReference, Entry>();
	private pending: Entry | undefined;
	private hydrationCleanup: (() => void) | undefined;
	private generation = 0;

	constructor(private readonly environment: IEnvironment) {}

	beginPreload(): number {
		this.hydrationCleanup?.();
		this.hydrationCleanup = undefined;
		this.releaseUnclaimed();
		return ++this.generation;
	}

	isCurrentPreload(generation: number): boolean {
		return this.generation === generation;
	}

	getHydrationVariables(): SpaceNavigationQuery$variables | undefined {
		const query = this.pending?.query;
		return query?.settled && !query.error && !query.disposed ? query.variables : undefined;
	}

	setHydrationCleanup(cleanup: () => void): void {
		this.hydrationCleanup?.();
		this.hydrationCleanup = cleanup;
	}

	get(variables: SpaceNavigationQuery$variables): SpaceNavigationQueryReference | undefined {
		return this.entries.get(
			createOperationDescriptor(SpaceNavigationQueryArtifact, variables).request.identifier,
		)?.query;
	}

	preload(variables: SpaceNavigationQuery$variables): SpaceNavigationQueryReference {
		const existing = this.get(variables);
		if (existing && !existing.disposed && !existing.error) return existing;

		this.releaseUnclaimed();
		const query = new SpaceNavigationQueryReference(this.environment, variables);
		const entry = { query, readers: 0 };
		this.entries.set(query.identifier, entry);
		this.entriesByReference.set(query, entry);
		this.pending = entry;
		return query;
	}

	retain(query: SpaceNavigationQueryReference): () => void {
		const entry = this.entriesByReference.get(query);
		if (!entry || query.disposed) {
			// A superseded render may reach its effect after its preload was cancelled.
			// It must not resurrect that execution or invalidate the current owner.
			return () => {};
		}
		entry.readers++;
		this.hydrationCleanup?.();
		this.hydrationCleanup = undefined;
		if (this.pending === entry) this.pending = undefined;
		let released = false;
		return () => {
			if (released) return;
			released = true;
			entry.readers--;
			// A Strict Mode effect replay can immediately acquire the same reference.
			queueMicrotask(() => {
				if (entry.readers === 0 && this.pending !== entry) {
					this.remove(entry);
				}
			});
		};
	}

	releaseUnclaimed(): void {
		if (this.pending && this.pending.readers === 0) {
			this.remove(this.pending);
		}
		this.pending = undefined;
	}

	private remove(entry: Entry): void {
		if (this.entries.get(entry.query.identifier) === entry) {
			this.entries.delete(entry.query.identifier);
		}
		entry.query.dispose();
	}
}

const owners = new WeakMap<IEnvironment, SpaceNavigationQueryOwner>();

/** Called by preload/bootstrap/event code, never during React render. */
export function getOrCreateSpaceNavigationQueryOwner(
	environment: IEnvironment,
): SpaceNavigationQueryOwner {
	let owner = owners.get(environment);
	if (!owner) {
		owner = new SpaceNavigationQueryOwner(environment);
		owners.set(environment, owner);
	}
	return owner;
}

/** Pure lookup for rendering; missing owners must be prepared outside render. */
export function getSpaceNavigationQueryOwner(
	environment: IEnvironment,
): SpaceNavigationQueryOwner | undefined {
	return owners.get(environment);
}
